import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import styles from "./FieldLabelButton.module.scss";

/**
 * Props for the FieldLabelButton component.
 * @extends ComponentPropsWithoutRef<"button">
 */
export type FieldLabelButtonProps = ComponentPropsWithoutRef<"button">;

/**
 * Internal icon-button for use within FieldLabel.
 *
 * Features:
 * - Default 28×28px hit area with no visible border or background at rest
 * - Optional `field-label-button-scaled` + CSS vars for larger icons (e.g. AiMark xlarge)
 * - Hover and focus states show the secondary button hover background color on
 *   an inner 24×24px area (or scaled inner), keeping 2px transparent on each side
 * - Focus ring matches the secondary button focus ring style
 * - Fully accessible as a native `<button>` element
 *
 * @example
 * <FieldLabelButton aria-label="More information" onClick={handleClick}>
 *   <Icon svg={Info} />
 * </FieldLabelButton>
 */
export const FieldLabelButton = forwardRef<
  HTMLButtonElement,
  FieldLabelButtonProps
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cx(styles["field-label-button"], className)}
    {...props}
  >
    <span className={styles["field-label-button__inner"]}>{children}</span>
  </button>
));

FieldLabelButton.displayName = "FieldLabelButton";
